import React from 'react';
import round from 'lodash/round';
import Button from '@mui/material/Button';
import GeneralChart from '@/components/city/GeneralChart';
import { CusEcharts } from '@/components/AsyncComponent';
import type { ECOption } from '@/components/CusEcharts';
import DataInterpretationSEO from '@/components/DataInterpretationSEO';
import { CityContext } from '@/context/create-context';
import { numberToUpper } from '@/utils/numbers';
import { TAB_INDUSTRY_TREND } from '@/constants/city';
import { trackClickEvent } from '@/utils/site-track';

const Chart = ({
  mouth,
  salary,
  name,
}: {
  mouth: string[];
  salary: number[];
  name: string;
}) => {
  const chartRef = React.useRef(null);
  const parseIntMonth = mouth.map((item) => parseInt(item.split('-')[1]));
  const roundSalary = salary.map((item) => round(item, -2));

  const config = React.useMemo<ECOption>(() => {
    return {
      grid: {
        show: false,
        tooltip: { show: true },
        left: 30,
        right: 30,
        top: 30,
        bottom: 20,
        borderWidth: 2,
        borderColor: '#f0f0f0',
        containLabel: false,
      },
      xAxis: {
        type: 'category',
        data: mouth,
        axisTick: { show: false },
        axisLine: { show: false },
        boundaryGap: false,
        axisLabel: {
          fontSize: 12,
          color: '#a3a3a3',
        },
      },
      yAxis: {
        type: 'value',
        show: false,
      },
      tooltip: {
        show: false,
        trigger: 'axis',
        axisPointer: {
          type: 'line',
          lineStyle: {
            type: 'solid',
            color: '#ea580c',
          },
        },
      },
      series: [
        {
          type: 'line',
          data: roundSalary,
          smooth: true,
          symbol: 'circle',
          symbolSize: 4,
          showSymbol: true,
          triggerLineEvent: false,
          color: '#ea580c',
          emphasis: {
            label: {
              show: true,
              fontSize: 12,
              color: 'rgba(0,0,0,0.6)',
              backgroundColor: 'transparent',
              formatter: ({ value }) => '¥' + value,
            },
          },
        },
      ],
    };
  }, [mouth, salary]);

  return (
    <>
      <p className="mb-4 text-sm leading-5.5 font-semibold">{`${
        parseIntMonth[parseIntMonth.length - 1]
      }月${name}类岗位平均月薪：¥${round(salary[salary.length - 1], -2)}`}</p>
      <div className="w-full h-[235px] [&_canvas]:cursor-default">
        <CusEcharts chartRef={chartRef} config={config} />
      </div>
    </>
  );
};

const IndustryTrend = () => {
  const { name: cityName, avg_by_month: avgByMonth } =
    React.useContext(CityContext);
  const [industryId, setIndustryId] = React.useState<string>(avgByMonth[0].id);

  const seoInfo = React.useMemo(() => {
    return (
      <>
        {avgByMonth.map(({ name, id, details }) => {
          const maxIndustry = Object.entries(details).reduce((a, b) =>
            a[1] > b[1] ? a : b,
          );
          const indusTryKeys = Object.keys(details);
          const indusTryValues = Object.values(details);
          const maxIndusIndex = indusTryKeys.findIndex(
            (item) => item === maxIndustry[0],
          );
          const getTrend = (data: number[]) => {
            return data[data.length - 1] - data[0] > 0 ? '上涨' : '下跌';
          };
          const trend =
            maxIndusIndex === 0
              ? getTrend(indusTryValues.splice(0, 3))
              : maxIndusIndex === indusTryKeys.length - 1
                ? getTrend(indusTryValues.splice(-3))
                : getTrend(
                    indusTryValues.slice(maxIndusIndex - 2, maxIndusIndex + 1),
                  );

          return (
            <div
              key={id}
              data-selected={id === industryId ? '' : undefined}
              className="h-0 overflow-hidden data-[selected]:w-auto data-[selected]:h-auto"
            >
              <DataInterpretationSEO className="text-opacity-85 mt-0">
                {`${cityName}${name}行业近${numberToUpper(
                  indusTryKeys.length,
                )}个月招聘市场的平均月薪如图：${parseInt(
                  maxIndustry[0].split('-')[1],
                )}月的平均月薪最高，为¥${round(
                  maxIndustry[1],
                  -2,
                )}，相较前几个月的薪资有一定的${trend}。曲线越向上代表薪资越高，数据来源依据于各平台公开薪酬数据，时间不同可能会产生波动，仅供参考。`}
              </DataInterpretationSEO>
            </div>
          );
        })}
      </>
    );
  }, [avgByMonth, industryId]);

  return (
    <GeneralChart title={`${cityName}招聘行业月薪变化趋势`} seoInfo="">
      <div className="mb-3 -mt-1 tablet:mb-5">{seoInfo}</div>
      <h3 className="flex mb-4 whitespace-nowrap overflow-auto">
        {avgByMonth.map(({ id, name }, index) => {
          return (
            <Button
              key={index}
              onClick={(e) => {
                setIndustryId(id);
                id !== industryId && trackClickEvent(e);
              }}
              data-selected={id === industryId ? '' : undefined}
              className="!px-6 !py-2 !text-black !bg-white/70 !font-semibold !text-sm !leading-5.5 !rounded-lg hover:!bg-black/[0.04] data-[selected]:hover:!bg-blue-50 data-[selected]:!bg-blue-50 data-[selected]:!text-web-blue-500"
              data-ele-group={`${TAB_INDUSTRY_TREND}_industry`}
              data-ele-id={`industry_${name}`}
            >
              {name}
            </Button>
          );
        })}
      </h3>
      <div className="w-full">
        {avgByMonth.map(({ id, name, details }) => (
          <div
            key={id}
            data-selected={id === industryId ? '' : undefined}
            className="h-0 overflow-hidden data-[selected]:w-auto data-[selected]:h-auto"
          >
            <Chart
              mouth={Object.keys(details)}
              salary={Object.values(details)}
              name={name}
            />
          </div>
        ))}
      </div>
    </GeneralChart>
  );
};

export default IndustryTrend;
